import React, { useState, useEffect } from 'react';
import {
  Typography,
  CircularProgress,
  Button,
  Grid
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { priceHabitationApi } from '@/api/stripeApi';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setPaymentType, setPrice } from '../habitationSlice';
import { useTranslation } from 'react-i18next';

interface PaymentSelectorProps {
  housingType: 'room' | 'full';
  numberOfRooms: number;
  onChange: (paymentType: 'mensuel' | 'annuel') => void;
  onSubmit: () => void;
}

const ButtonStyled = styled(Button)`
  background-color: #2f6975;
  color: white;
  border-radius: 30px;
  width: 150px;
  height: 35px;
  font-weight: 400;
  font-size: 15px;
  &:hover: {
    background-color: #26555f;
  }
  @media (max-width: 600px) {
      width: 100%;
      font-size: 13px;
      height: 30px;
  }
`;


const isStartDateOutdated = (isoStr: string | null) => {
  if (!isoStr) return false;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
};

const PaymentSelector = ({ housingType, numberOfRooms, onChange, onSubmit }: PaymentSelectorProps) => {
  const { t } = useTranslation('habitation');
  const { price, paymentType, startDate } = useAppSelector(state => state.habitation);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const fetchPriceCalledRef = React.useRef(false);
  const dateOutdated = isStartDateOutdated(startDate);

  const fetchPrice = async (type: 'mensuel' | 'annuel') => {
    setLoading(true);
    try {
      const response = await priceHabitationApi(type, housingType, numberOfRooms);
      dispatch(setPrice(response.data.priceAmount / 100));
      onChange(type);
    } catch (err: any) {
      // Ne pas logger si la requête a été annulée volontairement
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        console.error('Erreur lors de la récupération du prix :', err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Protection StrictMode : appeler une seule fois pour chaque combinaison de paramètres
    if (fetchPriceCalledRef.current) return;

    fetchPriceCalledRef.current = true;
    fetchPrice(paymentType);

    // Reset le flag si les dépendances changent pour permettre un nouvel appel
    return () => {
      fetchPriceCalledRef.current = false;
    };
  }, [housingType, numberOfRooms, paymentType]);

  const handleToggle = (type: 'mensuel' | 'annuel') => {
    if (type !== paymentType) {
      dispatch(setPaymentType(type));
      fetchPrice(type);
    }
  };

  return (
    <Grid container direction="column" alignItems="left" spacing={{xs: 2, sm: 5}} mt={{xs: 0, sm: 2}}>
      {/* Toggle buttons */}
      <Grid item sm={12}>
        <Grid container spacing={{xs: 2, sm: 0}} justifyContent="left">
          <Grid item sm={6} display="inline-flex" sx={{cursor: "pointer"}} onClick={() => handleToggle('mensuel')}>
                {paymentType === 'mensuel' ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <RadioButtonUncheckedIcon />
                )}

                <Typography variant="h5" ml={1} fontWeight={paymentType === 'mensuel' ? 500 : 300}>
                  MENSUEL
                </Typography>
          </Grid>
          <Grid item sm={6} display="inline-flex" sx={{cursor: "pointer"}} onClick={() => handleToggle('annuel')}>
                {paymentType === 'annuel' ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <RadioButtonUncheckedIcon />
                )}
                <Typography variant="h5" ml={1} fontWeight={paymentType === 'annuel' ? 500 : 300}>
                  ANNUEL
                </Typography>
          </Grid>
        </Grid>
      </Grid>

      {/* Price or loader */}
      <Grid item>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          price !== null && (
            <Typography fontSize={{xs: '22px', sm: '33px'}} fontWeight={500}>
              {price.toFixed(2).replace('.', ',')}€
            </Typography>
          )
        )}
      </Grid>

      {/* CTA Button */}
      <Grid item>
        <ButtonStyled
          disabled={loading || !startDate || dateOutdated}
          onClick={onSubmit}
          variant="contained"
        >
          {t('step5.subscribe')}
        </ButtonStyled>
      </Grid>
    </Grid>
  );
};

export default PaymentSelector;
